import { DateTime } from 'luxon';
import IPhoneIcon from '@app/assets/icons/iphone.svg?react';
import EmailIcon from '@app/assets/icons/mail-box.svg?react';
import CalendarIcon from '@app/assets/icons/calendar-01.svg?react';
import CalendarCheckIcon from '@app/assets/icons/calendar-check.svg?react';
import YelpIcon from '@app/assets/icons/integrations/yelp.svg?react';
import GoogleLogo from '@app/assets/icons/Google.svg?react';
import { CampaignInterface } from '@app/models/campaigns';
import { getCampaignStatusColor } from '@app/utils/utils';
import { CampaignMenu } from '@app/components';
import dayjs from 'dayjs';
import {
  cancelCampaign,
  cloneCampaign,
} from '@app/services/campaigns';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import { getBusinessIntegrations } from '@app/services/integrations';
import { IntegrationType } from '@app/utils/consts';

export const CampaignsTableRow = ({
  data,
  onClick,
  refetch,
  onDelete,
  isHighlighted,
  onHighlight,
  disabledActions,
}: {
  data: CampaignInterface;
  onClick?: () => void;
  refetch?: () => void;
  onDelete?: (campaign: CampaignInterface) => void;
  isHighlighted?: boolean;
  onHighlight?: (campaignId: string) => void;
  disabledActions?: {
    deleteCampaign?: boolean;
    cancelCampaign?: boolean;
    cloneCampaign?: boolean;
  };
}) => {
  // Fetch business integrations
  const { data: integrationsData } = useQuery({
    queryKey: ['business-integrations', data.business.id],
    queryFn: () => getBusinessIntegrations(data.business.id),
    enabled: !!data.business.id,
  });

  // Check which integrations are enabled
  const integrations = integrationsData?.data?.integrations || [];
  const hasGoogleIntegration = integrations.some((integration: { type: string }) => integration.type === IntegrationType.GOOGLE);
  const hasYelpIntegration = integrations.some((integration: { type: string }) => integration.type === IntegrationType.YELP);

  return (
    <tr
      className={`border-b border-base-antiFlash last-of-type:border-b-0 text-xs cursor-pointer hover:bg-brand-lightBlue4 transition-all duration-300 ${
        isHighlighted ? 'border-2 border-green-500' : ''
      }`}
      onClick={onClick}
    >
      <td className="p-4">
        <div className="flex gap-2">
          <div className="flex gap-1 flex-col">
            {data?.platforms?.split(',')?.map((t: string) =>
              t === 'email' ? (
                <span
                  key={t}
                  className="bg-base-antiFlash p-1 rounded-sm flex items-center justify-center"
                >
                  <EmailIcon className="w-4 h-4" />
                </span>
              ) : (
                <span
                  key={t}
                  className="bg-base-antiFlash p-1 rounded-sm h-6 w-6 flex items-center justify-center"
                >
                  <IPhoneIcon />
                </span>
              )
            )}
          </div>
          <div>
            <p className="font-bold">{data.name}</p>
            <p className="text-xs text-base-coolGray">#{data?.id}</p>
            <p className="text-xs text-base-coolGray">
              <span>Created on: </span>
              <span>
                {DateTime.fromISO(data.createdAt).toFormat('MMM dd, yyyy')}
              </span>
            </p>
          </div>
        </div>
      </td>
      <td className="p-4" style={{ textAlign: 'left' }}>
        <span
          className="w-2 h-2 inline-block rounded-full mr-1"
          style={{ backgroundColor: getCampaignStatusColor(data?.status) }}
        ></span>
        <span>
          {data?.status?.toLowerCase() === 'draft' 
            ? 'Drafts' 
            : data?.status?.charAt(0).toUpperCase() + data?.status?.slice(1).toLowerCase()
          }
        </span>
      </td>
      <td className="p-4" style={{ textAlign: 'left' }}>
        
          {(data?.schedules && data.schedules.length > 0 && (
            <div className="space-y-2">
              {data.schedules.map((schedule, idx) => {
                const isPastDate = dayjs(schedule.start).isBefore(dayjs());
                return (
                  <div key={schedule.id} className="flex items-center gap-2">
                    <div className={`p-1 rounded-sm flex items-center justify-center ${
                      isPastDate ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      {isPastDate ? (
                        <CalendarCheckIcon className="w-4 h-4" />
                      ) : (
                        <CalendarIcon className="w-4 h-4" />
                      )}
                    </div>
                    <span className="text-sm text-gray-700">
                      {isPastDate ? 'Sent on:' : (idx === 0 ? 'Initial send:' : 'Send again:')}
                    </span>
                    <span className="text-sm text-gray-700">
                      {dayjs(schedule.start).format('MMM DD, YYYY')}
                    </span>
                  </div>
                );
              })}
            </div>
          )
        )}
      </td>
      <td className="p-4" style={{ textAlign: 'left' }}>
        <div className="flex gap-2">
          {hasGoogleIntegration && (
            <div className="bg-white border border-gray-200 p-1 rounded-full flex items-center justify-center w-8 h-8">
              <GoogleLogo className="w-5 h-5" />
            </div>
          )}
          {hasYelpIntegration && (
            <div className="bg-white border border-gray-200 p-1 rounded-full flex items-center justify-center w-8 h-8">
              <YelpIcon className="w-5 h-5" />
            </div>
          )}
        </div>
      </td>
      <td className="p-4" style={{ textAlign: 'center' }}>
        <span className="font-semibold text-base">{data.customers.length}</span>
      </td>

      <td className="p-4">
        <CampaignMenu
          status={data.status}
          campaignId={data.id}
          deleteCampaign={async () => {
            onDelete?.(data);
          }}
          cloneCampaign={async () => {
            try {
              const response = await cloneCampaign(data.id, 'DRAFT');
              const clonedCampaign = response.data.campaign;
              toast.success('Campaign cloned successfully');
              refetch?.();
              onHighlight?.(clonedCampaign.id);
            } catch (error) {
              toast.error('Failed to clone campaign');
            }
          }}
          cancelCampaign={async () => {
            try {
              await cancelCampaign(data.id);
              toast.success('Campaign cancelled successfully');
              refetch?.();
            } catch (error) {
              toast.error('Failed to cancel campaign');
            }
          }}
          continueCreating={async () => {
            onClick?.();
          }}
          disabledActions={disabledActions}
        />
      </td>
    </tr>
  );
};
